import { z } from 'zod'

const sessionProfileSchema = z.looseObject({})

export type SessionProfile = z.infer<typeof sessionProfileSchema>

export function parseSessionProfile(payload: unknown): SessionProfile {
  return sessionProfileSchema.parse(payload)
}

export function findProfileDetails(
  payload: SessionProfile,
): Record<string, unknown> {
  const data = isRecord(payload.data) ? payload.data : null
  const candidates = [
    payload,
    data,
    payload.profile,
    payload.user,
    data?.profile,
    data?.user,
  ].filter(isRecord)

  return Object.assign({}, ...candidates)
}

export function readProfileText(
  profile: Record<string, unknown>,
  keys: readonly string[],
) {
  for (const key of keys) {
    const value = profile[key]
    if (typeof value === 'string' && value.trim()) return value.trim()
    if (typeof value === 'number') return String(value)
  }
  return ''
}

export function readProfileRole(profile: Record<string, unknown>) {
  const directRole = readProfileText(profile, [
    'roleName',
    'role_name',
    'role',
    'userRole',
  ])
  if (directRole) return directRole

  const roles = profile.roles
  if (!Array.isArray(roles)) return ''
  return roles
    .map((role) =>
      typeof role === 'string'
        ? role
        : isRecord(role)
          ? readProfileText(role, ['name', 'role', 'authority'])
          : '',
    )
    .filter(Boolean)
    .join(', ')
}

export function readProfileOrganization(profile: Record<string, unknown>) {
  const directOrganization = readProfileText(profile, [
    'organizationName',
    'organization_name',
    'organization',
    'companyName',
    'company_name',
    'tenantName',
    'tenant_name',
  ])
  if (directOrganization) return directOrganization

  const organization = profile.organization
  return isRecord(organization)
    ? readProfileText(organization, ['name', 'displayName'])
    : ''
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}
